import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import {
  getCertificateInfo,
  getTotalContribution,
  githubCallback,
} from '@/api/campaign/grass';
import type { GrassCampaignResponseDto } from '@/api/campaign/grass/grass.dto';
import { useLoginFailedModal } from '@/store/campaign/failed';

const GITHUB_DATA_KEY = 'github-user-data';
const ONE_HOUR = 1000 * 60 * 60;

function setStorageData(data: GrassCampaignResponseDto) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const expiredAt = Date.now() + ONE_HOUR;
    localStorage.setItem(GITHUB_DATA_KEY, JSON.stringify({ data, expiredAt }));
  } catch (error) {
    console.error('Failed to save data:', error);
    localStorage.removeItem(GITHUB_DATA_KEY);
  }
}

function getStorageData(): GrassCampaignResponseDto | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const item = localStorage.getItem(GITHUB_DATA_KEY);
    if (!item) {
      return null;
    }

    const { data, expiredAt } = JSON.parse(item);
    if (Date.now() > expiredAt) {
      localStorage.removeItem(GITHUB_DATA_KEY);
      localStorage.removeItem('loginAttempted');
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

export function useGithubData() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return useQuery({
    queryKey: ['campaign-grass'],
    queryFn: () => getStorageData(),
    initialData: getStorageData,
    enabled: isClient,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
}

export function useGithubCallback() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { open: loginFailedModalOpen } = useLoginFailedModal();

  return useMutation({
    mutationFn: githubCallback,
    onSuccess: async (data) => {
      const scrollPosition = parseInt(
        sessionStorage.getItem('scrollPosition') || '0',
      );

      localStorage.removeItem('loginAttempted');

      if (!data) {
        localStorage.removeItem(GITHUB_DATA_KEY);
        localStorage.removeItem('loginAttempted');
        queryClient.removeQueries({ queryKey: ['campaign-grass'] });

        loginFailedModalOpen();
        await router.replace('/campaign', undefined, { shallow: true });
        setTimeout(() => window.scrollTo(0, scrollPosition), 0);
        return;
      }

      setStorageData(data);
      queryClient.setQueryData(['grass-campaign'], data);

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ['campaign-total-contribution'],
        }),
        queryClient.refetchQueries({
          queryKey: ['campaign-grass'],
        }),
      ]);

      if (
        data.totalContributionsIn2024 === 0 &&
        !localStorage.getItem('loginAttempted')
      ) {
        loginFailedModalOpen();
        localStorage.setItem(
          'loginAttempted',
          JSON.stringify({
            value: true,
            expiredAt: Date.now() + ONE_HOUR,
          }),
        );
      }

      await router.replace('/campaign', undefined, { shallow: true });
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    },
    onError: async () => {
      localStorage.removeItem(GITHUB_DATA_KEY);
      localStorage.removeItem('loginAttempted');
      loginFailedModalOpen();
    },
  });
}

export function useTotalContribution() {
  return useQuery({
    queryKey: ['campaign-total-contribution'],
    queryFn: getTotalContribution,
    staleTime: ONE_HOUR,
  });
}

export function useCertificateInfo(id?: string) {
  return useQuery({
    queryKey: ['campaign-certificate-info', id],
    queryFn: () => getCertificateInfo(Number(id)),
    enabled: !!id,
  });
}
